import { useEffect, useRef, useState } from "react";

import type { ISwSettings } from "app/@types/ISwSettings";
import getIsDiscountActive from "app/components/discountCampaign/common/utils/getIsDiscountActive";
import {
    DISCOUNT_END_DATE,
    DISCOUNT_END_TIME_STORAGE_KEY,
    DISCOUNT_START_DATE,
} from "app/components/React/TrialBanner/DiscountBanner/utils/consts";
import { postUserDiscountDate } from "app/components/React/TrialBanner/DiscountBanner/utils/postUserDiscountDate";
import { getDiscountEndTime } from "app/components/timers/utils/helpers";
import { useFitScoreContext } from "app/contexts/FitScoreContext/FitScoreContext";
import { useSessionStorage } from "app/pages/activation/hooks/helpers/useSessionStorage";
import { isAfterTrial, isSI } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

interface IUseShowDiscountPopupApi {
    shouldShowDiscountPopup: boolean;
    setShouldShowDiscountPopup: (value: boolean) => void;
    discountEndTime: string | undefined;
}

export const useShowDiscountPopup = (swSettings: ISwSettings): IUseShowDiscountPopupApi => {
    const { isAllowed: isDiscountFitScoreAllowed } = useFitScoreContext();
    const [shouldShowDiscountPopup, setShouldShowDiscountPopup] = useState(false);
    const isPosting = useRef(false);
    const { getSessionStorage, setSessionStorage } = useSessionStorage();

    const discountEndTimeFromSettings = swSettings.user?.customData?.DiscountEndDate;
    const storageDiscountEndTime = getSessionStorage<string>(DISCOUNT_END_TIME_STORAGE_KEY);

    const discountEndTime = discountEndTimeFromSettings ?? storageDiscountEndTime;
    const userHasDiscount = !!discountEndTime;

    const isDiscountActive = getIsDiscountActive({ startDate: DISCOUNT_START_DATE, endDate: DISCOUNT_END_DATE });
    const isWebAfterTrial = isAfterTrial(swSettings) && !isSI(swSettings);

    useEffect(() => {
        const tryShowDiscount = async () => {
            if (
                !isDiscountFitScoreAllowed ||
                !isWebAfterTrial ||
                !isDiscountActive ||
                userHasDiscount ||
                isPosting.current
            ) {
                return;
            }

            isPosting.current = true;
            try {
                const newDiscountEndTime = getDiscountEndTime();
                const postSuccess = await postUserDiscountDate(newDiscountEndTime);
                if (postSuccess) {
                    setShouldShowDiscountPopup(true);
                    setSessionStorage(DISCOUNT_END_TIME_STORAGE_KEY, newDiscountEndTime);
                }
            } finally {
                isPosting.current = false;
            }
        };

        void tryShowDiscount();
    }, [
        isDiscountFitScoreAllowed,
        isWebAfterTrial,
        isDiscountActive,
        userHasDiscount,
        isPosting,
        setSessionStorage,
        getSessionStorage,
    ]);

    return {
        shouldShowDiscountPopup,
        setShouldShowDiscountPopup,
        discountEndTime,
    };
};
